import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Reveal from '../components/Reveal';
import { assets } from '../data/catalog';
import supabase from '../supabase/supabase';
import './AboutPage.css';

const {
  FiEye, FiTarget, FiLeaf, FiUsers, FiGlobe, FiCheck,
  FiAward, FiHeart, FiShield, FiChevronLeft, FiChevronRight
} = FiIcons;

const CERTIFICATE_IMAGES = [
  'https://cloud.sharemyimage.com/2026/08/24/Cert1.jpeg',
  'https://cloud.sharemyimage.com/2026/08/24/Cert2.jpeg',
  'https://cloud.sharemyimage.com/2026/08/24/Cert3.jpeg',
  'https://cloud.sharemyimage.com/2026/08/24/Cert4.jpeg',
  'https://cloud.sharemyimage.com/2026/08/24/Cert5.jpeg',
  'https://cloud.sharemyimage.com/2026/08/24/Cert6.jpeg',
  'https://cloud.sharemyimage.com/2026/08/24/Cert7.jpeg',
  'https://cloud.sharemyimage.com/2026/08/24/Cert8.jpeg',
  'https://cloud.sharemyimage.com/2026/08/24/Cert9.jpeg',
  'https://cloud.sharemyimage.com/2026/08/24/Cert10.jpeg'
];

const DEFAULT_CONTENT = {
  vision_english: 'To be a trusted global leader in 100% natural herbal wellness, inspiring healthy living worldwide while empowering rural Sri Lankan communities.',
  vision_sinhala: 'ස්වාභාවික ඖෂධීය නිෂ්පාදන හරහා ගෝලීය වශයෙන් නිරෝගී පරපුරක් නිර්මාණය කරමින්, ග්‍රාමීය ජනතාවගේ ආර්ථිකය සබලගන්වන ප්‍රමුඛතම ව්‍යවසාය බවට පත්වීම.',
  mission_english: 'To provide pure, chemical-free, and preservative-free natural health remedies to wellness-focused consumers globally, by directly sourcing dried flowers and herbal products from rural villagers to uplift their local economies.',
  mission_sinhala: 'කිසිදු රසායනික හෝ කල්තබා ගැනීමේ ද්‍රව්‍යයක් නොමැති 100% ස්වාභාවික වියළි මල් සහ ඖෂධීය නිෂ්පාදන ලොව පුරා නිරෝගී දිවියක් පතන ජනතාවට ලබාදීමත්, ග්‍රාමීය ජනතාවගෙන් සෘජුවම අමුද්‍රව්‍ය මිලදී ගනිමින් ඔවුන්ගේ ආර්ථික ජීවන මට්ටම උසස් කිරීමත් අපගේ මෙහෙවරයි.',
  value1_title: '100% Pure & Natural',
  value1_english: 'Pure botanical products with no artificial chemicals or preservatives.',
  value1_sinhala: 'කිසිදු කෘතිම රසායනික ද්‍රව්‍යයක් හෝ කල්තබා ගැනීමේ ද්‍රව්‍යයක් නොමැති පිරිසිදු ඖෂධීය ගුණය.',
  value2_title: 'Community Empowerment',
  value2_english: 'Supporting rural families through ethical sourcing and sustainable income.',
  value2_sinhala: 'ග්‍රාමීය ජනතාව සබලගන්වමින් ඔවුන්ගේ ආර්ථිකයට සෘජු දායකත්වයක් ලබාදීම.',
  value3_title: 'Global Wellness',
  value3_english: 'Delivering premium herbal wellness products to consumers across the world.',
  value3_sinhala: 'ලෝකය පුරා සිටින ජනතාවගේ නිරෝගී පැවැත්ම උදෙසා ගුණාත්මක ඖෂධීය නිෂ්පාදන සැපයීම.'
};

function AboutHero() {
  return (
    <section className="about-hero">
      <div className="about-hero-petals">
        <div className="about-hero-petal" />
        <div className="about-hero-petal" />
        <div className="about-hero-petal" />
        <div className="about-hero-petal" />
      </div>
      <div className="about-container">
        <div className="about-hero-grid">
          <Reveal className="about-hero-content">
            <span className="about-eyebrow">About Darshana Global Enterprises</span>
            <h1>Rooted in Nature,<br />Growing Communities.</h1>
            <p>Darshana Global Enterprises is a Sri Lankan botanical wellness company dedicated to producing 100% natural dried flowers, herbal teas and wellness ingredients while empowering rural communities through sustainable sourcing.</p>
            <Link to="/shop" className="button primary">Explore Our Story</Link>
          </Reveal>
          <Reveal className="about-hero-visual" delay={0.15}>
            <img src={assets.hero} alt="Premium botanical composition" />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function OurStory() {
  return (
    <section className="about-story">
      <div className="about-container">
        <div className="about-story-grid">
          <Reveal className="about-story-visual">
            <img src={assets.ingredientCircle} alt="Botanical illustration" />
          </Reveal>
          <Reveal className="about-story-content" delay={0.1}>
            <span className="about-eyebrow">Our Journey</span>
            <h2>How did our story begin?</h2>
            <p>Founded with a passion for natural wellness, Darshana Global Enterprises connects the richness of Sri Lanka's botanical heritage with wellness-conscious consumers around the world. Every flower and herbal ingredient is carefully sourced from rural villages, creating sustainable livelihoods while delivering exceptional natural products.</p>
            <div className="about-stats-row">
              <div className="about-stat-item">
                <h3>100%</h3>
                <p>Natural Products</p>
              </div>
              <div className="about-stat-item">
                <h3>Rural First</h3>
                <p>Community Sourcing</p>
              </div>
              <div className="about-stat-item">
                <h3>Premium</h3>
                <p>Export Quality</p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function VisionMission({ content }) {
  const c = content || DEFAULT_CONTENT;
  return (
    <section className="about-vision-mission">
      <div className="about-container">
        <Reveal className="about-vm-grid">
          <div className="about-vm-card">
            <div className="about-vm-icon">
              <SafeIcon icon={FiEye} />
            </div>
            <h3>Our Vision</h3>
            <p className="vm-english">
              {c.vision_english}
            </p>
            <div className="about-vm-divider" />
            <p className="vm-sinhala">{c.vision_sinhala}</p>
          </div>
          <div className="about-vm-card">
            <div className="about-vm-icon">
              <SafeIcon icon={FiTarget} />
            </div>
            <h3>Our Mission</h3>
            <p className="vm-english">
              {c.mission_english}
            </p>
            <div className="about-vm-divider" />
            <p className="vm-sinhala">{c.mission_sinhala}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function CoreValues({ content }) {
  const c = content || DEFAULT_CONTENT;
  const values = [
    {
      icon: FiLeaf,
      title: c.value1_title,
      english: c.value1_english,
      sinhala: c.value1_sinhala
    },
    {
      icon: FiUsers,
      title: c.value2_title,
      english: c.value2_english,
      sinhala: c.value2_sinhala
    },
    {
      icon: FiGlobe,
      title: c.value3_title,
      english: c.value3_english,
      sinhala: c.value3_sinhala
    }
  ];

  return (
    <section className="about-values">
      <div className="about-container">
        <Reveal className="about-values-header">
          <span className="about-eyebrow">What drives us</span>
          <h2>What values guide our work?</h2>
          <div className="about-divider" />
        </Reveal>
        <div className="about-values-grid">
          {values.map((value, i) => (
            <Reveal key={value.title} delay={i * 0.1}>
              <div className="about-value-card">
                <div className="about-value-icon">
                  <SafeIcon icon={value.icon} />
                </div>
                <h3>{value.title}</h3>
                <p className="value-english">{value.english}</p>
                <p className="value-sinhala">{value.sinhala}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function LeadershipTeam() {
  const team = [
    {
      name: 'P.M. Prasanna',
      role: 'Managing Director',
      image: 'https://cloud.sharemyimage.com/2026/08/23/Prasannaimg.webp'
    },
    {
      name: 'Kanchana Dayarathna',
      role: 'Director',
      image: 'https://cloud.sharemyimage.com/2026/08/24/46555BAB-CC2D-4821-BED2-BBC4D65A530E.webp'
    },
    {
      name: 'Madushika Nandasiri',
      role: 'Manager',
      image: 'https://cloud.sharemyimage.com/2026/08/23/Madushikaimg.webp'
    }
  ];

  return (
    <section className="about-team">
      <div className="about-container">
        <Reveal className="about-team-header">
          <span className="about-eyebrow">The people behind Darshana</span>
          <h2>Meet Our Leadership Team</h2>
          <p>Dedicated professionals committed to natural wellness and sustainable community development.</p>
        </Reveal>
        <div className="about-team-grid">
          {team.map((member, i) => (
            <Reveal key={member.name} delay={i * 0.12}>
              <div className="about-team-card">
                <div className="about-team-photo">
                  <img src={member.image} alt={member.name} />
                </div>
                <div className="about-team-info">
                  <h3>{member.name}</h3>
                  <span>{member.role}</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Certifications() {
  const features = [
    { icon: FiAward, text: 'Premium Quality Standards' },
    { icon: FiShield, text: 'Responsible Herbal Processing' },
    { icon: FiCheck, text: 'Trusted Botanical Manufacturing' }
  ];

  return (
    <section className="about-certifications">
      <div className="about-container">
        <div className="about-cert-grid">
          <Reveal className="about-cert-frame">
            <img
              src="https://cloud.sharemyimage.com/2026/08/23/Certificate.jpg"
              alt="Company certification"
            />
          </Reveal>
          <Reveal className="about-cert-content" delay={0.1}>
            <span className="about-eyebrow">Certifications</span>
            <h2>Certified Quality You Can Trust</h2>
            <p>Our commitment to quality, authenticity and responsible manufacturing is reflected through our certifications and continuous dedication to delivering premium natural botanical products.</p>
            <div className="about-cert-features">
              {features.map((item) => (
                <div className="about-cert-feature" key={item.text}>
                  <div className="about-cert-feature-icon">
                    <SafeIcon icon={item.icon} />
                  </div>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function CertificationsSlider() {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? CERTIFICATE_IMAGES.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === CERTIFICATE_IMAGES.length - 1 ? 0 : prev + 1));
  };

  return (
    <section className="about-cert-slider-section">
      <div className="about-container">
        <Reveal className="about-cert-slider-header">
          <span className="about-eyebrow">Our Credentials</span>
          <h2>Our Certifications</h2>
          <p>Recognized for quality, authenticity, and responsible manufacturing practices.</p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="about-cert-slider">
            <button className="cert-slider-btn cert-slider-prev" onClick={goToPrev} aria-label="Previous certificate">
              <SafeIcon icon={FiChevronLeft} />
            </button>
            <div className="cert-slider-viewport">
              <div className="cert-slider-track" style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
                {CERTIFICATE_IMAGES.map((img, i) => (
                  <div className="cert-slider-slide" key={i}>
                    <img src={img} alt={`Certification ${i + 1}`} />
                  </div>
                ))}
              </div>
            </div>
            <button className="cert-slider-btn cert-slider-next" onClick={goToNext} aria-label="Next certificate">
              <SafeIcon icon={FiChevronRight} />
            </button>
          </div>
          <div className="cert-slider-dots">
            {CERTIFICATE_IMAGES.map((_, i) => (
              <button
                key={i}
                className={`cert-dot ${i === currentIndex ? 'active' : ''}`}
                onClick={() => setCurrentIndex(i)}
                aria-label={`Go to certificate ${i + 1}`}
              />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function CommunityImpact() {
  const stats = [
    { value: '100%', label: 'Natural Herbal Products' },
    { value: 'Rural First', label: 'Direct Community Sourcing' },
    { value: 'Global Vision', label: 'Serving Wellness Worldwide' }
  ];

  return (
    <section className="about-community">
      <div className="about-container">
        <Reveal className="about-community-header">
          <span className="about-eyebrow">Making a difference</span>
          <h2>From Village to World</h2>
          <div className="about-divider" />
        </Reveal>
        <Reveal className="about-impact-stats" delay={0.1}>
          {stats.map((stat) => (
            <div className="about-impact-card" key={stat.label}>
              <h3>{stat.value}</h3>
              <p>{stat.label}</p>
            </div>
          ))}
        </Reveal>
        <Reveal delay={0.2}>
          <p className="about-community-text">
            Every purchase contributes directly to empowering rural Sri Lankan communities by creating sustainable opportunities for local growers and herbal producers.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function BrandPhilosophy() {
  return (
    <section className="about-philosophy">
      <div className="about-philosophy-petals">
        <div className="about-philosophy-petal" />
        <div className="about-philosophy-petal" />
      </div>
      <div className="about-container">
        <Reveal className="about-philosophy-content">
          <span className="about-eyebrow">Our Promise</span>
          <h2>Brand Philosophy</h2>
          <div className="about-philosophy-divider" />
          <p className="about-tagline">"Pure Nature, Empowered Lives."</p>
          <div className="about-philosophy-divider" />
          <p className="about-tagline">"100% Natural Herbal Care from Village to World."</p>
          <p>
            Nature and community remain at the heart of every product we create. From the villages of Sri Lanka to wellness-conscious homes around the world, our commitment to purity, sustainability and authenticity never wavers.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function AboutCTA() {
  return (
    <section className="about-cta">
      <div className="about-container">
        <Reveal className="about-cta-card">
          <span className="about-eyebrow">Begin your journey</span>
          <h2>Experience the Purity of Nature</h2>
          <p>Discover our collection of naturally dried flowers, herbal teas and botanical wellness products crafted with care from the heart of Sri Lanka.</p>
          <div className="about-cta-buttons">
            <Link to="/shop" className="button primary">Shop Collection</Link>
            <a href="mailto:hello@darshana.lk" className="button secondary">Contact Us</a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function AboutPage() {
  const [content, setContent] = useState(DEFAULT_CONTENT);

  useEffect(() => {
    const loadContent = async () => {
      const { data } = await supabase.from('about_content_1750000006').select('*');
      if (data && data.length > 0) {
        const map = { ...DEFAULT_CONTENT };
        data.forEach((item) => {
          if (item.content_value) {
            map[item.content_key] = item.content_value;
          }
        });
        setContent(map);
      }
    };
    loadContent();
  }, []);

  return (
    <main className="about-page">
      <AboutHero />
      <OurStory />
      <VisionMission content={content} />
      <CoreValues content={content} />
      <LeadershipTeam />
      <Certifications />
      <CertificationsSlider />
      <CommunityImpact />
      <BrandPhilosophy />
      <AboutCTA />
    </main>
  );
}

export default AboutPage;